import ResizeObserver from 'resize-observer-polyfill';
import EmbedScriptEvents from '../EmbedScriptEvents';

class EventRouter {
	constructor() {
		this._interfaceMap = new Map();
		this._resizeObserver = null;

		this._globalMessageHandler = this.handleGlobalMessageEvent.bind(this);
		this._clickEventHandler = () => this.sendToAll(EmbedScriptEvents.HOST_CLICK);
		this._forgetEventHandler = () => this.sendToAll(EmbedScriptEvents.FORGET);
		this._logoutEventHandler = () => this.sendToAll(EmbedScriptEvents.LOGOUT);
		this._loginEventHandler = () =>
			this.sendToAll(EmbedScriptEvents.LOGIN, {
				hostname: window.location.hostname
			});
	}
	/**
	 * Adds new interface to internal map of interfaces.
	 *
	 * @param {SznDiscussion} instance
	 */
	register(instance) {
		if (this._interfaceMap.size === 0) {
			this._registerGlobalEvents();
		}

		this._interfaceMap.set(instance.sentinel, instance);
	}

	/**
	 * Unregsters interface from internal map by given instance sentinel.
	 *
	 * @param {SznDiscussion} instance
	 */
	unregister(instance) {
		this._interfaceMap.delete(instance.sentinel);

		if (this._interfaceMap.size === 0) {
			this._unregisterGlobalEvents();
		}
	}

	/**
	 * Sends event with payload to all instances.
	 *
	 * @param {string} eventName
	 * @param {*} payload
	 */
	sendToAll(eventName, payload) {
		for (const instance of this._interfaceMap.values()) {
			instance.sendEvent(eventName, payload);
		}
	}

	/**
	 * Sends event with payload to all other instances.
	 *
	 * @param {string} thisSentinel
	 * @param {string} eventName
	 * @param {*} payload
	 */
	sendToOthers(thisSentinel, eventName, payload) {
		for (const instance of this._interfaceMap.values()) {
			if (instance.sentinel !== thisSentinel) {
				instance.sendEvent(eventName, payload);
			}
		}
	}

	/**
	 * Sends event with payload to specific instance
	 * @param {string} sentinel
	 * @param {string} eventName
	 * @param {*} payload
	 */
	sendToInstance(sentinel, eventName, payload) {
		const instance = this._interfaceMap.get(sentinel);

		instance && instance.sendEvent(eventName, payload);
	}

	/**
	 * Passes event with payload to specific instance to be handled there.
	 *
	 * @param {MessageEvent} event
	 */
	handleGlobalMessageEvent(event) {
		const { type, sentinel, payload } = event.data;
		const instance = this._interfaceMap.get(sentinel);

		instance && instance.handleMessage(type, payload);
	}

	handleDocumentResize() {
		for (const instance of this._interfaceMap.values()) {
			instance.handleDocumentResize();
		}
	}

	_registerGlobalEvents() {
		window.addEventListener('message', this._globalMessageHandler);
		window.addEventListener('mouseup', this._clickEventHandler);
		window.addEventListener('forget', this._forgetEventHandler);
		window.addEventListener('logout', this._logoutEventHandler);
		window.addEventListener('login', this._loginEventHandler);

		let lastObservedRect = {};

		this._resizeObserver = new ResizeObserver(entries => {
			const observedEntry = entries[0];
			const observedRect = observedEntry.contentRect;

			if (!this._areClientRectsEqual(lastObservedRect, observedRect)) {
				lastObservedRect = {
					bottom: observedRect.bottom,
					height: observedRect.height,
					left: observedRect.left,
					right: observedRect.right,
					top: observedRect.top,
					width: observedRect.width,
					x: observedRect.x,
					y: observedRect.y
				};

				this.handleDocumentResize();
			}
		});

		this._resizeObserver.observe(document.body);
	}

	_unregisterGlobalEvents() {
		window.removeEventListener('message', this._globalMessageHandler);
		window.removeEventListener('mouseup', this._clickEventHandler);
		window.removeEventListener('forget', this._forgetEventHandler);
		window.removeEventListener('logout', this._logoutEventHandler);
		window.removeEventListener('login', this._loginEventHandler);

		this._resizeObserver.disconnect();
	}

	/**
	 * Returns client rects and return's true if they're the same.
	 *
	 * @param {DOMRectReadOnly} a
	 * @param {DOMRectReadOnly} b
	 * @returns {boolean}
	 */
	_areClientRectsEqual(a, b) {
		return (
			a.bottom === b.bottom &&
			a.height === b.height &&
			a.left === b.left &&
			a.right === b.right &&
			a.top === b.top &&
			a.width === b.width &&
			a.x === b.x &&
			a.y === b.y
		);
	}
}

export default new EventRouter();
